import type { Project } from '../../projects/types';

/* ─── Helpers ────────────────────────────────────────── */

function formatOT(clientNumber?: number | null, projectNumber?: number | null): string | null {
  if (!clientNumber || !projectNumber) return null;
  return `${String(clientNumber).padStart(4, '0')}-${String(projectNumber).padStart(2, '0')}`;
}

function hasBounds(p: Project): boolean {
  return p.bounds.maxLng - p.bounds.minLng >= 0.001 && p.bounds.maxLat - p.bounds.minLat >= 0.001;
}

/* ─── Props ──────────────────────────────────────────── */

interface ProjectInfoCardProps {
  project: Project;
  onEnter: () => void;
  onClose: () => void;
}

/* ─── Component ──────────────────────────────────────── */

/**
 * ProjectInfoCard — Ficha flotante de proyecto para la Consola Territorial.
 *
 * Posicionada en la esquina derecha del mapa vía .sa-project-card (absolute).
 * El padre (.sa-map-wrapper) tiene position: relative.
 */
export function ProjectInfoCard({ project, onEnter, onClose }: ProjectInfoCardProps): JSX.Element {
  const ot = formatOT(project.clientNumber, project.projectNumber);
  const moduleCount = project.activeModules?.length ?? 0;
  const bounded = hasBounds(project);

  return (
    <aside className="sa-project-card" aria-label={`Ficha: ${project.name}`}>
      {/* Header: OT + tenant + close */}
      <div className="sa-project-card-header">
        <div className="sa-project-card-meta">
          {ot && <span className="sa-project-card-ot">OT {ot}</span>}
          {project.tenant?.name && (
            <span className="sa-project-card-tenant">{project.tenant.name}</span>
          )}
        </div>
        <button className="sa-project-card-close" onClick={onClose} aria-label="Cerrar ficha">
          ✕
        </button>
      </div>

      {/* Project name */}
      <h3 className="sa-project-card-name">{project.name}</h3>

      {/* Stats row */}
      <div className="sa-project-card-stats">
        <div className="sa-project-card-stat">
          <span className="sa-project-card-stat-value">{moduleCount}</span>
          <span className="sa-project-card-stat-label">módulos activos</span>
        </div>
        <div className="sa-project-card-stat">
          <span
            className={`sa-project-card-stat-value${bounded ? '' : ' sa-project-card-stat-value--muted'}`}
          >
            {bounded ? '✓ Geo' : 'Sin geo'}
          </span>
          <span className="sa-project-card-stat-label">cobertura territorial</span>
        </div>
      </div>

      {/* Active modules tags */}
      {moduleCount > 0 && (
        <div className="sa-project-card-modules">
          {(project.activeModules ?? []).map(m => (
            <span key={m} className="sa-project-card-module-tag">
              {m}
            </span>
          ))}
        </div>
      )}

      {/* Enter button */}
      <button className="sa-project-card-enter" onClick={onEnter}>
        Entrar al proyecto
      </button>
    </aside>
  );
}
